import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Typography, Space, Card, Button } from "antd";
import { useSelector, useDispatch } from "react-redux";
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title as ChartTitle,
} from "chart.js";
import { getLocationChartData, getInstituteChartData, getDegreeChartData, getMetricsData, getStagesChartData } from "../actions";
import moment from "moment";

// Import the new components using relative paths
import OverallAndLatestRolesp2 from './components/dashboard/OverallAndLatestRolesp2';
import DashboardFilters from './components/dashboard/DashboardFilters';
import StatsRow from './components/dashboard/StatsRow';
import DegreeAnalysis from './components/dashboard/DegreeAnalysis';
import Stages from "./components/dashboard/Stages";
import LocationAnalysis from './components/dashboard/LocationAnalysis';
import InstitueBar from "./components/dashboard/InstitueBar";
import InstituteGender from "./components/dashboard/InstituteGender";
import ExportPDF from "./exportPdf";
import LoadingOverlay from "./components/LoadingOverlay";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartTitle);

const { Title } = Typography;


const CorporateDashboardContainerp2 = () => {
  console.log('CorporateDashboardContainerp2 rendered');
  const dispatch = useDispatch();
  const locationChartData = useSelector(state => state.corporateDashboardPage2?.locationChartData);
  const instituteChartData = useSelector(state => state.corporateDashboardPage2?.instituteChartData);

  // State needed by DashboardFilters is kept here
  const [selectedFilter, setSelectedFilter] = useState(null);

  // Initialize financial year with current financial year
  const getCurrentFinancialYearRange = () => {
    const currentYear = moment().year();
    const currentMonth = moment().month() + 1;

    let financialYear;
    if (currentMonth < 4) {
      financialYear = currentYear - 1;
    } else {
      financialYear = currentYear;
    }

    return [
      moment(`${financialYear}-04-01`),
      moment(`${financialYear + 1}-03-31`)
    ];
  };

  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getCurrentFinancialYearRange());

  // State for institute selection
  const [selectedInstitute, setSelectedInstitute] = useState(null);

  // State for applicants/hires toggle
  const [isApplied, setIsApplied] = useState(true);

  // State for tracking which bar was clicked (applicants or hires)
  const [selectedBarType, setSelectedBarType] = useState('applicants');

  // Handle filter change with selection clear
  const handleFilterChange = (newFilter) => {
    // Clear institute selection when filter changes
    setSelectedInstitute(null);
    setSelectedFilter(newFilter);
  };

  // Handle financial year change
  const handleFinancialYearChange = (financialYear) => {
    setSelectedFinancialYear(financialYear);
  };

  // State for location type
  const [isPermanentLocation, setIsPermanentLocation] = useState(false);

  // State for cities vs states toggle (isState: false = cities, true = states)
  const [isState, setIsState] = useState(false);

  // Handle institute click from bar chart
  const handleInstituteClick = (event, element, datasetInfo) => {
    if (element && element.length > 0) {
      const { index } = element[0];
      const { isApplicantsBar, isHiresBar } = datasetInfo || {};

      // Use unique ID for selection since all institute names are "Not Specified"
      const uniqueId = `institute_${index}`;
      setSelectedInstitute(uniqueId);
      
      // Store which bar was clicked for gender analysis
      setSelectedBarType(isApplicantsBar ? 'applicants' : 'hires');
    }
  };
  // Fetch location chart data when component mounts, filter changes, location type changes, toggle changes, or financial year changes
  useEffect(() => {
    dispatch(getLocationChartData(selectedFilter || "JOBS", isPermanentLocation, isState, selectedFinancialYear));
  }, [dispatch, selectedFilter, isPermanentLocation, isState, selectedFinancialYear]);

  // Fetch institute chart data on component mount and when filter or financial year changes
  useEffect(() => {
    dispatch(getInstituteChartData(selectedFilter || "JOBS", selectedFinancialYear));
  }, [dispatch, selectedFilter, selectedFinancialYear]);

  // Fetch degree chart data when filter or financial year changes
  useEffect(() => {
    dispatch(getDegreeChartData(selectedFilter || "JOBS", selectedFinancialYear));
  }, [dispatch, selectedFilter, selectedFinancialYear]);

  // Fetch metrics data when filter or financial year changes
  useEffect(() => {
    dispatch(getMetricsData(selectedFilter || "JOBS", selectedFinancialYear));
  }, [dispatch, selectedFilter, selectedFinancialYear]);

  // Fetch stages data when filter or financial year changes
  useEffect(() => {
    dispatch(getStagesChartData(selectedFilter || "JOBS", selectedFinancialYear));
  }, [dispatch, selectedFilter, selectedFinancialYear]);

  // Handle location type changes from HorizontalBar
  const handleLocationTypeChange = (isPermanent) => {
    setIsPermanentLocation(isPermanent);
  };

  // Handle cities vs states toggle from HorizontalBar
  const handleToggleChange = (showCities) => {
    setIsState(!showCities); // isState is opposite of showCities
  };

  const handleClearInstituteSelection = () => {
    setSelectedInstitute(null);
  };
  // Handle applicants/hires toggle change
  const handleApplicantsToggleChange = (checked) => {
    // Clear institute selection when toggle changes
    setSelectedInstitute(null);
    setIsApplied(checked);
  };
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [individualExportLoading, setIndividualExportLoading] = useState(false)
  const [exportMessage, setExportMessage] = useState('Exporting...')
  const degreeRef = useRef(null)
  const degreeDataRef = useRef([])
  const setChartRangeRef = useRef(null)
  const topLocationsRef = useRef(null)
  const topInstitutesRef = useRef(null)
  const stages = useRef(null)
  const stagesDataRef = useRef([])
  const exportRefs = {
    internshipRoles: degreeRef,
    internshipRolesData: degreeDataRef,
    setChartRange: setChartRangeRef,
    topLocations: topLocationsRef,
    topInstitutes: topInstitutesRef,
    stages: stages,
    stagesData: stagesDataRef,
  }

  return (
    <div style={{
      padding: "24px",
      background: "#ffffffd8",
      height: "calc(100vh - 48px)",
      overflowY: "auto"
    }}>
      {/* Styles can stay here or move to a separate CSS/StyledComponent file */}
      <style>{`
        .cd-card { border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); background: #fff; }
        .cd-stat-card { border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); background: #fff; height: 60px; display:flex; align-items:center; padding:8px 16px; }
      `}</style>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={1}>Dashboard</Title>
          </Col>
          <Col>
            <Button
              type="default"
              onClick={() => setIsExportOpen(true)}
              style={{
                borderColor: "#52c41a",
                color: "#0FB680",
                backgroundColor: "#f5f5f5"
              }}
            >
              Export
            </Button>
          </Col>
        </Row>

        {/* Render the components */}
        <OverallAndLatestRolesp2
          selectedFilter={selectedFilter}
          setSelectedFilter={handleFilterChange}
        />

        <DashboardFilters
          selectedFilter={selectedFilter}
          setSelectedFilter={handleFilterChange}
          onFinancialYearChange={handleFinancialYearChange}
          initialFinancialYear={selectedFinancialYear}
        />
        <StatsRow selectedFilter={selectedFilter} />

        <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
          <Col span={24}>
            <div ref={degreeRef}>
              <DegreeAnalysis 
                selectedFilter={selectedFilter} 
                isExporting={isExportOpen} 
                degreeDataRef={degreeDataRef} 
                setChartRangeRef={setChartRangeRef}
                setIndividualExportLoading={setIndividualExportLoading}
                setExportMessage={setExportMessage}
              />
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
          <Col span={24}>
            <div ref={topLocationsRef}>
              <LocationAnalysis
                locationChartData={locationChartData}
                selectedFilter={selectedFilter}
                onLocationTypeChange={handleLocationTypeChange}
                onToggleChange={handleToggleChange}
                topLocationsRef={topLocationsRef}
                setIndividualExportLoading={setIndividualExportLoading}
                setExportMessage={setExportMessage}
                loading={individualExportLoading}
              />
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
          <Col span={24}>
            <div ref={topInstitutesRef}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  padding: '0 0px 16px 0px',
                }}
              >
                <Row
                  gutter={[16, 16]}
                  align="stretch"
                  justify="space-between"
                  style={{ width: '100%' }}
                >
                  {/* LEFT: Horizontal Bar Chart */}
                  <Col xs={24} lg={12}>
                    <div style={{
                      width: '100%',
                      overflow: 'auto',
                      alignItems: 'flex-start',
                      padding: '0 0px',
                      // minHeight: '450px'
                      height: '100%'

                    }}>
                      <InstitueBar
                        selectedInstitute={selectedInstitute}
                        onInstituteClick={handleInstituteClick}
                        onClearSelection={handleClearInstituteSelection}
                        selectedFilter={selectedFilter}
                        isApplied={isApplied}
                        onApplicantsToggleChange={handleApplicantsToggleChange}
                      />
                    </div>
                  </Col>

                  <Col xs={24} lg={12}>
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        padding: '0 8px',
                        height: '100%'
                      }}
                    >
                      <InstituteGender
                        selectedInstitute={selectedInstitute}
                        selectedFilter={selectedFilter}
                        topInstitutesRef={topInstitutesRef}
                        setIndividualExportLoading={setIndividualExportLoading}
                        setExportMessage={setExportMessage}
                        isApplied={isApplied}
                        selectedBarType={selectedBarType}
                      />
                    </div>
                  </Col>

                </Row>
              </Card>
            </div>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
          <Col span={24}>
            <div ref={stages}>
              <Stages 
                selectedFilter={selectedFilter} 
                isExporting={isExportOpen} 
                stagesDataRef={stagesDataRef} 
                stagesRef={stages}
                setIndividualExportLoading={setIndividualExportLoading}
                setExportMessage={setExportMessage}
              />
            </div>
          </Col>
        </Row>

      </Space>
      <ExportPDF
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        exportRefs={exportRefs}
        selectedFilter={selectedFilter}
        isPermanentLocation={isPermanentLocation} />
      <LoadingOverlay 
        visible={individualExportLoading} 
        message={exportMessage} 
      />
    </div>
  );
};


export default CorporateDashboardContainerp2;